/**
 * App shell: resolve which table we are at, keep a live SSE view of it, and
 * swap between the home, lobby and table screens. All game logic lives in
 * `@manille/core`; all authority lives on the server.
 */

import type { GameAction, PlayerView, Seat } from '@manille/core';

import {
  clearLastGameId,
  deviceToken,
  lastGameId,
  setLastGameId,
  setStoredName,
  storedName,
} from './device.js';
import { type ConnectionStatus, connect, createGame, joinGame, sendAction } from './net.js';
import { preloadCards } from './ui/card.js';
import { el } from './ui/dom.js';
import { mountHome } from './ui/home.js';
import { type LobbyActions, type LobbyScreen, mountLobby } from './ui/lobby.js';
import { type TableActions, type TableScreen, mountTable } from './ui/table.js';
import { toast } from './ui/toast.js';

const appRoot = document.querySelector<HTMLElement>('#app');
if (!appRoot) throw new Error('#app is missing');
const app: HTMLElement = appRoot;

let gameId: string | null = null;
let disconnect: (() => void) | null = null;
let lobby: LobbyScreen | null = null;
let table: TableScreen | null = null;
let status: ConnectionStatus = 'connecting';
let view: PlayerView | null = null;

const statusBar = el('div', 'status-bar');
statusBar.hidden = true;
document.body.append(statusBar);

function act(action: GameAction): void {
  if (!gameId) return;
  void sendAction(gameId, action).then((response) => {
    if (!response.ok) toast(response.message, 'warn');
  });
}

const tableActions: TableActions = {
  play: (card) => act({ type: 'play-card', card }),
  declare: (trump) => act({ type: 'declare', trump }),
  double: (raise) => act({ type: 'double', double: raise }),
  nextDeal: () => act({ type: 'next-deal' }),
  rematch: () => act({ type: 'rematch' }),
  leave: () => {
    // Forget the table first, or boot() walks straight back into it.
    clearLastGameId();
    location.href = '/';
  },
};

const lobbyActions: LobbyActions = {
  start: () => act({ type: 'start-match' }),
  setBot: (seat: Seat, bot: boolean) => act({ type: 'set-bot', seat, bot }),
  setName: (name: string) => {
    setStoredName(name);
    act({ type: 'set-name', name });
  },
  leave: tableActions.leave,
};

function showHome(): void {
  teardown();
  app.append(
    mountHome(
      {
        create: async (name) => {
          setStoredName(name);
          try {
            const id = await createGame(name || 'Host');
            enterGame(id);
          } catch {
            toast('Server niet bereikbaar.', 'warn');
          }
        },
        join: async (code, name) => {
          setStoredName(name);
          const response = await joinGame(code, name || 'Speler');
          if (!response.ok) {
            toast(response.message, 'warn');
            return;
          }
          enterGame(code);
        },
      },
      storedName(),
    ),
  );
}

/** Shown for the moment between joining and the first view arriving. */
function joiningScreen(id: string): HTMLElement {
  const panel = el('div', 'panel panel--joining');
  const deck = el('div', 'joining__deck');
  for (let i = 0; i < 3; i += 1) {
    const card = el('div', 'joining__card');
    card.style.setProperty('--i', String(i));
    deck.append(card);
  }
  const bail = el('button', 'btn btn--ghost', 'Annuleren');
  bail.addEventListener('click', () => {
    clearLastGameId();
    location.href = '/';
  });
  panel.append(
    deck,
    el('h1', 'panel__title', 'Plaatsnemen…'),
    el('p', 'panel__sub', `Table ${id}`),
    bail,
  );
  return panel;
}

function teardown(): void {
  disconnect?.();
  disconnect = null;
  lobby?.destroy();
  lobby = null;
  table?.destroy();
  table = null;
  view = null;
  app.replaceChildren();
}

function enterGame(id: string): void {
  gameId = id.toUpperCase();
  setLastGameId(gameId);
  history.replaceState(null, '', `/?g=${gameId}`);
  teardown();
  app.append(joiningScreen(gameId));
  disconnect = connect(gameId, render, setStatus);
}

function setStatus(next: ConnectionStatus): void {
  status = next;
  statusBar.hidden = status === 'live';
  // Tappable: a lost stream must never be a dead end.
  statusBar.textContent =
    status === 'connecting' ? 'Opnieuw verbinden…' : 'Verbinding weg — tik om te verlaten';
  statusBar.classList.toggle('status-bar--lost', status === 'lost');
}

statusBar.addEventListener('click', () => {
  if (status === 'live') return;
  clearLastGameId();
  location.href = '/';
});

function render(next: PlayerView): void {
  // Out-of-order frames would make the table jump backwards.
  if (view && next.version < view.version) return;
  const first = view === null;
  view = next;

  if (next.phase === 'lobby') {
    if (table) {
      table.destroy();
      table = null;
    }
    if (!lobby) {
      app.replaceChildren();
      lobby = mountLobby(lobbyActions);
      app.append(lobby.root);
    }
    lobby.update(next);
    return;
  }

  if (lobby) {
    lobby.destroy();
    lobby = null;
  }
  if (!table) {
    app.replaceChildren();
    table = mountTable(tableActions);
    app.append(table.root);
  }
  table.update(next);
  if (first && next.you.seat !== null) toast(`Je zit op stoel ${next.you.seat + 1}.`);
}

async function boot(): Promise<void> {
  const requested = new URL(location.href).searchParams.get('g');
  const id = (requested ?? '').toUpperCase();
  if (!id) {
    // Coming back to a table you were already at is the common case on mobile.
    const previous = lastGameId();
    if (previous) {
      const response = await joinGame(previous, storedName());
      if (response.ok) {
        enterGame(previous);
        return;
      }
    }
    showHome();
    return;
  }

  const response = await joinGame(id, storedName() || 'Speler');
  if (!response.ok) {
    // Any failure — table full, or the game is simply gone because the server
    // restarted — has to land on the home screen. Entering anyway left the app
    // stuck on "Joining…" against a table that does not exist.
    toast(response.message || 'Die tafel bestaat niet meer.', 'warn');
    clearLastGameId();
    history.replaceState(null, '', '/');
    showHome();
    return;
  }
  enterGame(id);
}

// Reclaiming the seat after the phone locks or the tab is backgrounded.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && gameId && status !== 'live') {
    disconnect?.();
    disconnect = connect(gameId, render, setStatus);
  }
});

void deviceToken();
void boot();
// After boot, not before: the join round-trip is what decides whether anything
// is on screen at all, and 32 card images should not be queued ahead of it.
preloadCards();

if ('serviceWorker' in navigator && location.protocol === 'https:') {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      /* offline shell is a nicety, not a requirement */
    });
  });
}
